import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { PublicUser } from '@sirocco/shared';
import { messages as messagesApi } from '../../api/endpoints';
import { Avatar, GroupAvatar } from '../ui/Avatar';
import { useSession } from '../../store/SessionContext';

/**
 * La banniere des messages, dans l'application.
 *
 * iOS n'affiche aucune notification quand l'application est au premier
 * plan : c'est le seul moment ou l'on est certain que le membre regarde
 * son telephone, et le seul ou rien ne lui parvenait. Un message arrivait
 * alors en silence, et il fallait aller voir la pastille pour l'apprendre.
 *
 * Elle ne ressemble pas a celle du systeme, et c'est voulu : ici on tient
 * son telephone, donc le texte du message s'affiche. La notification
 * distante, elle, se contente du nom — elle peut s'afficher sur un ecran
 * verrouille que n'importe qui regarde.
 *
 * **Elle se tait dans la conversation concernee.** Annoncer un message
 * qui vient de s'afficher trois centimetres plus bas serait du bruit.
 *
 * Elle s'appuie sur le compteur de non-lus, deja ecoute par la barre
 * d'onglets : une branche de quelques nombres. Quand un compte monte, on
 * lit le dernier message de ce fil-la — une lecture, et seulement quand
 * il y a quelque chose a annoncer.
 */

/** Le temps qu'elle reste, si on n'y touche pas. */
const DUREE_MS = 4200;

interface Annonce {
  conversationId: string;
  titre: string;
  auteur: PublicUser;
  texte: string;
  groupe: boolean;
}

export function NotifEnApp() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [annonce, setAnnonce] = useState<Annonce | null>(null);
  const [sortante, setSortante] = useState(false);
  const minuteur = useRef(0);

  /*
    Le balayage vers le haut.

    C'est le geste qu'on essaie devant une banniere, et il ne demande rien
    a apprendre. Vers le haut seulement : elle vient de la, elle y
    retourne. Vers le bas, elle resiste — la tirer ouvrirait sur rien.
  */
  const [glisse, setGlisse] = useState(0);
  const [tenue, setTenue] = useState(false);
  /*
    Elle revient a sa place apres un balayage abandonne.

    Un etat a part, et non « le glissement vaut zero » : au montage aussi
    il vaut zero, et la classe de retour coupe l'animation d'entree. La
    banniere paraissait alors d'un coup, sans descendre — c'etait
    exactement le defaut signale.
  */
  const [revient, setRevient] = useState(false);
  const retour = useRef(0);
  const depart = useRef<number | null>(null);

  function onDown(event: PointerEvent<HTMLButtonElement>) {
    depart.current = event.clientY;
    setTenue(true);
    // Le doigt tient la banniere : elle ne doit pas s'effacer sous lui.
    window.clearTimeout(minuteur.current);
  }

  function onMove(event: PointerEvent<HTMLButtonElement>) {
    if (depart.current === null) return;
    setGlisse(Math.min(0, event.clientY - depart.current));
  }

  function onUp() {
    if (depart.current === null) return;
    depart.current = null;
    setTenue(false);

    // Quarante pixels : de quoi distinguer un balayage d'un appui qui
    // tremble.
    if (glisse < -40) {
      setAnnonce(null);
      setGlisse(0);
      return;
    }
    setGlisse(0);
    setRevient(true);
    window.clearTimeout(retour.current);
    retour.current = window.setTimeout(() => setRevient(false), 280);
    minuteur.current = window.setTimeout(() => setSortante(true), DUREE_MS);
  }

  /*
    L'etat precedent des compteurs.

    C'est la comparaison qui fait l'evenement : un compteur qui monte veut
    dire qu'un message est arrive. La premiere lecture ne compte pas —
    sinon ouvrir l'application annoncerait tout ce qui attendait.
  */
  const connus = useRef<Record<string, number> | null>(null);
  const routeRef = useRef(pathname);
  routeRef.current = pathname;

  useEffect(() => {
    if (!user) {
      connus.current = null;
      return;
    }

    return messagesApi.watchNonLusParFil((compteurs) => {
      const avant = connus.current;
      connus.current = compteurs;
      if (!avant) return;

      const monte = Object.entries(compteurs).find(
        ([conversationId, compte]) => compte > (avant[conversationId] ?? 0),
      );
      if (!monte) return;

      const [conversationId] = monte;
      // Deja dans le fil : le message s'affiche tout seul, trois
      // centimetres plus bas.
      if (routeRef.current.startsWith(`/app/messages/${conversationId}`)) return;

      void messagesApi
        .apercu(conversationId)
        .then((vu) => {
          if (!vu) return;
          setSortante(false);
          setAnnonce({ conversationId, ...vu });
        })
        .catch(() => undefined);
    });
  }, [user]);

  /* Elle s'efface d'elle-meme, en glissant vers le haut. */
  useEffect(() => () => window.clearTimeout(retour.current), []);

  useEffect(() => {
    if (!annonce) return;
    window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(() => setSortante(true), DUREE_MS);
    return () => window.clearTimeout(minuteur.current);
  }, [annonce]);

  if (!annonce) return null;

  return (
    <button
      type="button"
      className={`notif-app${sortante ? ' notif-app--sort' : ''}${
        tenue ? ' notif-app--tenue' : revient ? ' notif-app--revient' : ''
      }`}
      style={glisse ? { transform: `translateY(${glisse}px)` } : undefined}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onAnimationEnd={() => sortante && setAnnonce(null)}
      onClick={() => {
        // Un balayage n'est pas un appui : sans cela, la faire glisser
        // ouvrirait la conversation a l'instant ou on la chasse.
        if (glisse !== 0) return;
        setAnnonce(null);
        navigate(`/app/messages/${annonce.conversationId}`);
      }}
    >
      {annonce.groupe ? (
        <GroupAvatar size={40} rounded />
      ) : (
        <Avatar user={annonce.auteur} size={40} />
      )}
      <span className="notif-app__corps">
        <span className="notif-app__nom">{annonce.titre}</span>
        <span className="notif-app__texte">
          {annonce.groupe ? `${annonce.auteur.username} : ${annonce.texte}` : annonce.texte}
        </span>
      </span>
    </button>
  );
}
